import {useAccessToken} from "../../services/auth"
import React, {useCallback, useEffect, useState} from "react"
import {
	getSamlSettingsByToken,
	OrgSamlSettings,
	patchSamlSettings,
	SignatureAlgorithm,
} from "../../resources/orgSettings"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useToasts} from "react-toast-notifications"
import {optionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"

function SamlSettingsForm({
	doc,
	accessToken,
	refresh,
	onSamlSaved,
}: {
	doc: OrgSamlSettings
	accessToken: string
	refresh: () => void
	onSamlSaved: () => void
}) {
	const [state, patch] = useAsyncResultIdle(patchSamlSettings)
	const saml = doc.attributes.samlSettings
	const [orgWorkspace, setOrgWorkspace] = useState(saml?.orgWorkspace ?? "")
	const [entryPoint, setEntryPoint] = useState(saml?.entryPoint ?? "")
	const [signatureAlgorithm, setSignatureAlgorithm] = useState<SignatureAlgorithm>(saml?.signatureAlgorithm ?? "sha1")
	const [cert, setCert] = useState(saml?.cert ?? "")
	const [enforceSSOLogin, setEnforceSSOLogin] = useState(saml?.enforceSSOLogin)
	const {addToast} = useToasts()

	useEffect(() => {
		const next = doc.attributes.samlSettings
		setOrgWorkspace(next?.orgWorkspace ?? "")
		setEntryPoint(next?.entryPoint ?? "")
		setSignatureAlgorithm(next?.signatureAlgorithm ?? "sha1")
		setCert(next?.cert ?? "")
		setEnforceSSOLogin(next?.enforceSSOLogin)
	}, [doc])

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Settings Updated Successfully", {appearance: "success"})
			onSamlSaved()
			refresh()
		}
	}, [state])

	return (
		<div className={"org-settings-content"}>
			<h3> SAML </h3>
			<form
				onSubmit={(e) => {
					e.preventDefault()
					if (orgWorkspace && entryPoint && cert && signatureAlgorithm) {
						patch(accessToken, {
							orgWorkspace: orgWorkspace,
							entryPoint: entryPoint,
							cert: cert,
							signatureAlgorithm: signatureAlgorithm,
							enforceSSOLogin: enforceSSOLogin ?? false,
						})
					}
				}}
			>
				<fieldset>
					<div className="field">
						<label className="label">Workspace Unique Name / Identifier</label>
						<div className="control">
							<input
								className="input"
								type="text"
								value={orgWorkspace}
								required
								onChange={(e) => setOrgWorkspace(e.target.value)}
							/>
						</div>
					</div>
					<div className="field">
						<label className="label">Identity Provider Entry-point</label>
						<div className="control">
							<input
								className="input"
								type="url"
								value={entryPoint}
								required
								onChange={(e) => setEntryPoint(e.target.value)}
							/>
						</div>
					</div>
					<div className="field">
						<label className="label">Signature Algorithm</label>
						<div className="control">
							<select
								value={signatureAlgorithm}
								onChange={(e) => setSignatureAlgorithm(e.target.value as SignatureAlgorithm)}
							>
								<option key="sha1" value="sha1">
									sha1
								</option>
								<option key="sha256" value="sha256">
									sha256
								</option>
								<option key="sha512" value="sha512">
									sha512
								</option>
							</select>
						</div>
					</div>
					<div className="field">
						<label className="label">Identity Provider Certificate</label>
						<div className="control">
							<textarea
								placeholder="Certificate"
								className="textarea has-fixed-size"
								value={cert}
								required
								onChange={(e) => setCert(e.target.value)}
							/>
						</div>
					</div>
					<div className="field">
						<label className="label">
							<input
								type="checkbox"
								checked={enforceSSOLogin ?? false}
								onChange={(e) => setEnforceSSOLogin(e.target.checked)}
							/>
							&nbsp;Enforce SSO Login
						</label>
					</div>
				</fieldset>
				<div className="columns">
					<div className="column mt-4">
						{state.match(
							() => (
								<button className="button is-success">Update</button>
							),
							() => (
								<button className="button is-success is-loading">Updating</button>
							),
							(_) => null,
							(err) => (
								<>
									<div className="has-text-danger">{err.errors[0].title}</div>
									<button className="button is-success">Update</button>
								</>
							)
						)}
					</div>
				</div>
			</form>
			<hr />
			<label className="label">Unit (Service Provider) Settings:</label>
			{optionalHorizontalField(
				"Assertion URL",
				`${process.env.API_URL}/sso/saml/login/callback/${orgWorkspace ? orgWorkspace : "<workspace>"}`
			)}
			{optionalHorizontalField(
				"Issuer",
				`${process.env.API_URL}/sso/saml/metadata/${orgWorkspace ? orgWorkspace : "<workspace>"}`
			)}
		</div>
	)
}

export default function SamlSettings({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()
	const [reloadKey, setReloadKey] = useState(0)
	const bumpReload = useCallback(() => setReloadKey((k) => k + 1), [])
	const samlAsync = useAsyncResult(() => getSamlSettingsByToken(accessToken), [accessToken, reloadKey])

	return (
		<div className={"card"}>
			<AsyncResultComponent
				asyncResult={samlAsync}
				pendingComponent={
					<div className={"org-settings-content"}>
						<h3> SAML </h3>
						<p className="help">Loading SAML settings…</p>
					</div>
				}
			>
				{({value: doc}) => (
					<SamlSettingsForm doc={doc} accessToken={accessToken} refresh={refresh} onSamlSaved={bumpReload} />
				)}
			</AsyncResultComponent>
		</div>
	)
}
